import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { getSortField, getSortValue, sortByName, sortKey } from '../../../../../../features/table/helpers';
import { formatDateTimeRangeFromString } from '../../../../../../helpers/date-time';
import {
  buildExternalMonitoringPeriod,
  formatExternalMonitoringPeriodLabelRu,
} from '../../../../../../helpers/external-monitoring-period';
import { useLang } from '../../../../../../hooks/use-lang';
import { useQueryParams } from '../../../../../../hooks/use-query-params';
import {
  EXTERNAL_REVIEW_LANGUAGE_PENDING,
  ExternalReviewItemType,
  FetchExternalReviewItemsRequest,
  displayLabelForStoredCommentLanguageCode,
} from '../../../../../../store/api/external-reviews-items-api';
import {
  fetchExternalResourceLanguages,
  type ExternalResourceLanguage,
} from '../../../../../../store/api/monitoring-api';
import { useExternalReviewItemsStore } from '../../../../../../store/use-external-review-items-store';

export const useExternalReviews = () => {
  const {
    fetchExternalReviewItems,
    externalReviewItems,
    isLoadingFetch,
  } = useExternalReviewItemsStore();
  const { t } = useLang();
  const { getQueryParams } = useQueryParams();
  const params = getQueryParams();

  const [externalResourceLanguages, setExternalResourceLanguages] = useState<ExternalResourceLanguage[]>(
    [],
  );

  useEffect(() => {
    fetchExternalResourceLanguages()
      .then(setExternalResourceLanguages)
      .catch(() => setExternalResourceLanguages([]));
  }, []);

  const source = params['source'];
  const comment = params['comment'];
  const reviewLanguage = params['review_language'];
  const sentiment = params['sentiment'];
  const createdAt = params['created_at'];
  const commentsPeriod = params['comments_period'] ?? '1';
  const commentsBeginParam = params['comments_begin'];
  const commentsEndParam = params['comments_end'];
  const sort = params[sortKey];
  const sortField = getSortField(sort);
  const sortDirection = getSortValue(sort);

  /** Запрос без page/page_size и без сортировки: полный список один раз; сортировка на клиенте */
  const fetchParams: FetchExternalReviewItemsRequest = useMemo(() => {
    const parsedCreatedAt = formatDateTimeRangeFromString(createdAt);
    const hasColumnRange = !!(parsedCreatedAt?.from && parsedCreatedAt?.to);

    if (hasColumnRange) {
      return {
        source,
        comment,
        review_language: reviewLanguage || undefined,
        sentiment: sentiment as 'positive' | 'negative' | 'mixed' | undefined,
        from: parsedCreatedAt!.from,
        to: parsedCreatedAt!.to,
      };
    }

    const nb = commentsBeginParam != null ? Number(commentsBeginParam) : NaN;
    const ne = commentsEndParam != null ? Number(commentsEndParam) : NaN;
    if (Number.isFinite(nb) && Number.isFinite(ne) && nb <= ne) {
      return {
        source,
        comment,
        review_language: reviewLanguage || undefined,
        sentiment: sentiment as 'positive' | 'negative' | 'mixed' | undefined,
        begin: nb,
        end: ne,
      };
    }

    const { begin, end } = buildExternalMonitoringPeriod(commentsPeriod);
    return {
      source,
      comment,
      review_language: reviewLanguage || undefined,
      sentiment: sentiment as 'positive' | 'negative' | 'mixed' | undefined,
      begin,
      end,
    };
  }, [
    source,
    comment,
    reviewLanguage,
    sentiment,
    createdAt,
    commentsPeriod,
    commentsBeginParam,
    commentsEndParam,
  ]);

  useEffect(() => {
    fetchExternalReviewItems(fetchParams);
  }, [fetchParams, fetchExternalReviewItems]);

  /** Доп. фильтрация по URL (данные уже с сервера целиком) */
  const externalReviewItemsFiltered = useMemo(() => {
    let list = externalReviewItems.filter((i) => i.language_code !== 'junk');
    if (comment) {
      const q = comment.toLowerCase();
      list = list.filter((i) => i.comment.toLowerCase().includes(q));
    }
    if (sentiment === 'positive' || sentiment === 'negative' || sentiment === 'mixed') {
      list = list.filter((i) => !i.sentiment_in_progress && i.sentiment === sentiment);
    }
    if (source) {
      const q = source.toLowerCase();
      list = list.filter((i) => i.source?.name?.toLowerCase().includes(q));
    }
    if (reviewLanguage) {
      if (reviewLanguage === EXTERNAL_REVIEW_LANGUAGE_PENDING) {
        list = list.filter((i) => i.language_id === null);
      } else {
        const q = reviewLanguage.trim().toLowerCase();
        list = list.filter((i) => i.language_code === q);
      }
    }
    return list;
  }, [externalReviewItems, comment, reviewLanguage, sentiment, source]);

  const externalReviewItemsSorted = useMemo(() => {
    if (!sortField || !sortDirection) return externalReviewItemsFiltered;

    const list: ExternalReviewItemType[] = [...externalReviewItemsFiltered];
    const dir = sortDirection;

    const sentimentRank = (i: ExternalReviewItemType) => {
      if (i.sentiment_in_progress) return 4;
      const s = i.sentiment;
      return s === 'positive' ? 0 : s === 'mixed' ? 1 : s === 'negative' ? 2 : 3;
    };

    list.sort((a, b) => {
      switch (sortField) {
        case 'source':
          return sortByName(a.source?.name ?? '', b.source?.name ?? '', dir);
        case 'comment':
          return sortByName(a.comment, b.comment, dir);
        case 'review_language': {
          const label = (i: ExternalReviewItemType) =>
            i.language_id === null ? t('В процессе') : i.language_label || '';
          return sortByName(label(a), label(b), dir);
        }
        case 'sentiment': {
          const d = sentimentRank(a) - sentimentRank(b);
          return dir === 'asc' ? d : -d;
        }
        case 'created_at': {
          const ta = dayjs(a.created_at).valueOf();
          const tb = dayjs(b.created_at).valueOf();
          if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
          if (Number.isNaN(ta)) return 1;
          if (Number.isNaN(tb)) return -1;
          return dir === 'asc' ? ta - tb : tb - ta;
        }
        default:
          return 0;
      }
    });

    return list;
  }, [externalReviewItemsFiltered, sortField, sortDirection, t]);

  const usesCreatedAtColumnRange = useMemo(() => {
    const parsed = formatDateTimeRangeFromString(createdAt);
    return !!(parsed?.from && parsed?.to);
  }, [createdAt]);

  const periodHintText = useMemo(() => {
    if (usesCreatedAtColumnRange) {
      return t('Используется диапазон из фильтра колонки «Дата»');
    }
    const nb = commentsBeginParam != null ? Number(commentsBeginParam) : NaN;
    const ne = commentsEndParam != null ? Number(commentsEndParam) : NaN;
    if (Number.isFinite(nb) && Number.isFinite(ne) && nb <= ne) {
      return formatExternalMonitoringPeriodLabelRu(nb, ne);
    }
    return buildExternalMonitoringPeriod(commentsPeriod).labelRu;
  }, [usesCreatedAtColumnRange, commentsPeriod, commentsBeginParam, commentsEndParam, t]);

  /** Границы для datepicker периода (когда не задействован фильтр колонки «Дата»). */
  const { tablePeriodBeginSec, tablePeriodEndSec } = useMemo(() => {
    if (usesCreatedAtColumnRange) {
      return { tablePeriodBeginSec: 0, tablePeriodEndSec: 0 };
    }
    const nb = commentsBeginParam != null ? Number(commentsBeginParam) : NaN;
    const ne = commentsEndParam != null ? Number(commentsEndParam) : NaN;
    if (Number.isFinite(nb) && Number.isFinite(ne) && nb <= ne) {
      return { tablePeriodBeginSec: nb, tablePeriodEndSec: ne };
    }
    const { begin, end } = buildExternalMonitoringPeriod(commentsPeriod);
    return { tablePeriodBeginSec: begin, tablePeriodEndSec: end };
  }, [usesCreatedAtColumnRange, commentsBeginParam, commentsEndParam, commentsPeriod]);

  const languageFilterOptions = useMemo(() => {
    const fromApi = [...externalResourceLanguages]
      .filter((l) => l.name.trim().toLowerCase() !== 'junk')
      .sort((a, b) =>
        displayLabelForStoredCommentLanguageCode(a.name).localeCompare(
          displayLabelForStoredCommentLanguageCode(b.name),
          'ru',
        ),
      )
      .map((l) => ({
        id: l.name.trim().toLowerCase(),
        value: displayLabelForStoredCommentLanguageCode(l.name),
      }));
    return [
      { id: EXTERNAL_REVIEW_LANGUAGE_PENDING, value: t('В процессе') },
      ...fromApi,
    ];
  }, [externalResourceLanguages, t]);

  return {
    externalReviewItems: externalReviewItemsSorted,
    isLoadingFetch,
    t,
    periodHintText,
    usesCreatedAtColumnRange,
    tablePeriodBeginSec,
    tablePeriodEndSec,
    /** GET external-resource/languages */
    externalResourceLanguages,
    languageFilterOptions,
  };
};
